import { useCan, useI18n } from '#imports'

export const useMenu = () => {
  const { t } = useI18n()

  const menuConfig = [
    {
      label: 'menu.home',
      icon: 'heroicons:home',
      link: '/',
      access: true,
    },
    {
      label: 'menu.accounts',
      icon: 'heroicons:users',
      link: '/warehousesman/1',
      access: 'admin-warehouseman.view',
    },
    {
      label: 'menu.warehousemans',
      icon: 'majesticons:users-line',
      link: '/warehousesman/1',
      access: 'warehouseman.view',
    },

    {
      label: 'menu.warehouseman-user',
      icon: 'heroicons:users',
      link: '/warehouseman-user/1',
      access: 'warehouseman-user.view',
    },
    {
      label: 'menu.acceptance',
      icon: 'heroicons:package-plus',
      link: '/warehouse-task/acceptance/1',
      access: "warehouse-task-acceptance.view",
    },
    {
      label: 'menu.moving-acceptance',
      icon: 'heroicons:move',
      link: '/warehouse-task/moving-acceptance/1',
      access: 'warehouse-task-acceptance-moving.view',
    },
    {
      label: 'menu.orders',
      icon: 'heroicons:shopping-bag',
      access: 'order.view',
      addition: [
        {
          label: 'menu.orders-menu.with-products',
          link: '/orders/1',
          access: 'order.view',
        },
        {
          label: 'menu.orders-menu.grouped',
          link: '/orders/grouped',
          access: 'order.view',
        },
      ],
    },
    {
      label: 'menu.selection',
      icon: 'streamline:shipment-check',
      link: '/warehouse-task/selection/1',
      access: 'warehouse-task-selection.view',
    },
    {
      label: 'menu.supply',
      icon: 'carbon:scis-transparent-supply',
      link: '/supply/1',
      access: 'supply.view',
    },
    {
      label: 'menu.shipment',
      icon: 'lucide:truck',
      link: '/shipment',
      access: ['shipment.view', 'supply.view'],
    },

    {
      label: 'menu.warehouse',
      icon: 'heroicons:boxes',
      access: ['warehouse-cell.view', 'warehouse.view', 'product.view'],
      addition: [
        {
          label: 'menu.warehouse-cell',
          link: '/warehouse-cell/1',
          access: 'warehouse-cell.view',
        },
        {
          label: 'menu.warehouses',
          link: '/warehouses/1',
          access: 'warehouse.view',
        },
        {
          label: 'menu.products',
          link: '/products/1',
          access: 'product.view',
        },
        {
          label: 'menu.inventory',
          link: '/product-inventory',
          access: 'product-inventory.view',
        },
        {
          label: 'menu.productsInCell',
          link: '/products-in-cell/1',
          access: 'product-inventory.view',
        },
        {
          label: 'menu.kizPool',
          link: '/kiz/pool',
          access: 'warehouse-task-selection.view',
        },
      ],
    },
    {
      label: 'menu.leads',
      icon: 'heroicons:copy-check',
      access: ['lead.view'],
      addition: [
        {
          label: 'menu.lead-menu.list',
          link: '/lead/1',
          access: 'lead.view',
        },
        {
          label: 'menu.lead-menu.kanban',
          link: '/kanban',
          access: 'lead.view',
        },
      ],
    },
    {
      label: 'menu.clients',
      icon: 'heroicons:briefcase',
      link: '/clients/1',
      access: 'client.view',
    },
    {
      label: 'menu.settings',
      icon: 'heroicons:settings',
      access: [
        'admin-region-type.view',
        'warehouse-cell.view',
        'product-type.view',
        'board.view',
      ],
      addition: [
        {
          label: 'menu.lookups.settings',
          expanded: true,
          children:[
            {
              label: 'menu.roles',
              link: '/roles/1',
              access: 'role.view',
            },
            {
              label: 'menu.components',
              link: '/ui',
              access: true,
            },
            {
              label: 'menu.acceptanceTest',
              link: '/acceptance',
              access: true,
            },
            {
              label: 'menu.warehouse-service',
              link: '/warehouse-service/1',
              access: true,
            },
          ]
        },
        {
          label: 'menu.lookups.title',
          expanded: true,
          children: [
            {
              label: 'menu.lookups.region_type',
              link: '/lookup/region-type/1',
              access: 'admin-region-type.view',
            },
            {
              label: 'menu.lookups.lead-task-type',
              link: '/lookup/lead-task-type/1',
              access: 'lead-task.view',
            },
            {
              label: 'menu.lookups.product-type',
              link: '/lookup/product-type/1',
              access: 'product-type.view',
            },
            {
              label: 'menu.lookups.board',
              link: '/lookup/board/1',
              access: 'board.view',
            },
            {
              label: 'menu.lookups.client-service',
              link: '/lookup/client-service/1',
              access: 'client-service.view',
            }
          ],
        },
      ],
    },
  ]

  function resolveAccess(rule) {
    if (typeof rule === 'boolean') return rule
    if (typeof rule === 'string') return useCan(rule)
    if (Array.isArray(rule)) return rule.some(resolveAccess)
    return false
  }

  function flattenChildren(addition) {
    const result = []
    for (const item of addition) {
      if (item.children && item.children.length > 0) {
        for (const child of item.children) {
          if (child.children && child.children.length > 0) {
            result.push(...child.children)
          } else {
            result.push(child)
          }
        }
      } else {
        result.push(item)
      }
    }
    return result
  }

  function translateAndResolve(node) {
    return {
      ...node,
      label: node.label ? t(node.label) : undefined,
      access: resolveAccess(node.access),
      addition: node.addition
        ? node.addition.map(translateAndResolve)
        : undefined,
      children: node.children
        ? node.children.map(translateAndResolve)
        : undefined,
    }
  }

  function transformItem(node) {
    if (!resolveAccess(node.access)) return null

    const item = {
      label: node.label ? t(node.label) : undefined,
      icon: node.icon,
    }

    if (node.link) {
      item.to = node.link
    }

    if (node.addition && node.addition.length > 0) {
      const children = flattenChildren(node.addition)
        .filter((child) => resolveAccess(child.access))
        .map((child) => ({
          label: child.label ? t(child.label) : undefined,
          icon: child.icon,
          to: child.link,
        }))

      if (children.length > 0) {
        item.children = children
      }
    }

    return item
  }

  const items = computed(() => menuConfig.map(translateAndResolve))

  const navItems = computed(() =>
    menuConfig.map(transformItem).filter(Boolean)
  )

  const sections = computed(() => {
    const all = navItems.value
    return [
      {
        label: '',
        items: all.filter((i) => i.icon === 'heroicons:home'),
      },
      {
        label: '',
        items: all.filter((i) =>
          ['heroicons:package-plus', 'heroicons:move', 'streamline:shipment-check', 'heroicons:shopping-bag', 'carbon:scis-transparent-supply', 'lucide:truck'].includes(i.icon)
        ),
      },
      {
        label: '',
        items: all.filter((i) =>
          ['heroicons:boxes', 'lucide:table-cells-merge', 'heroicons:briefcase', 'heroicons:copy-check'].includes(i.icon)
        ),
      },
      {
        label: '',
        items: all.filter((i) => i.icon === 'heroicons:settings'),
      },
      {
        label: '',
        items: all.filter((i) =>
          ['heroicons:users', 'majesticons:users-line'].includes(i.icon)
        ),
      },
    ].filter((s) => s.items.length > 0)
  })

  return { items, navItems, sections }
}
